import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable } from 'rxjs';
import { SALES_DASHBOARD_URL } from '../shared/src';
import { ErrorHandlerService } from './error-handler.service';

@Injectable({
    providedIn: 'root',
})
export class SalesDashboardService {
    constructor(
        private http: HttpClient,
        private errorHandler: ErrorHandlerService
    ) { }

    private readonly SALES_DASHBOARD_API = `${SALES_DASHBOARD_URL}`;

    // Sales Performance Dashboard APIs

    public getRegions(): Observable<any> {
        return this.http
            .get<any>(`${this.SALES_DASHBOARD_API}/salesdashboard/regions`)
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getCountries(region?: string | number | Array<string | number>): Observable<any> {

        let queryParams = new HttpParams();
        if (region !== undefined && region !== null && region !== '' && region !== 0 && !(Array.isArray(region) && region.length === 0)) {
            if (Array.isArray(region)) {
                // join array into comma-separated list
                const val = region.join(',');
                queryParams = queryParams.set('region', val);
            } else {
                queryParams = queryParams.set('region', String(region));
            }
        }
        return this.http
            .get<any>(`${this.SALES_DASHBOARD_API}/salesdashboard/countries`, { params: queryParams })
            .pipe(
                map((res) => res?.result ?? res),
                catchError(this.errorHandler.handleError),
            );
    }

    public getCurrencies(countryCode?: string | string[]): Observable<any> {
        let queryParams = new HttpParams();
        if (countryCode) {
            queryParams = queryParams.set('countryCode', String(countryCode));
        }

        return this.http
            .get<any>(`${this.SALES_DASHBOARD_API}/salesdashboard/currencies`, { params: queryParams })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getProductLines(countryCode?: string | string[]): Observable<any> {
        let queryParams = new HttpParams();
        if (countryCode) {
            queryParams = queryParams.set('countryCode', String(countryCode));
        }
        return this.http
            .get<any>(`${this.SALES_DASHBOARD_API}/salesdashboard/product-lines`, { params: queryParams })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getCurrencyByCountry(countryCode?: string | string[] | undefined): Observable<any> {
        let queryParams = new HttpParams()
        if (countryCode) {
            queryParams = queryParams.set('countryCode', String(countryCode));
        }

        return this.http
            .get<any>(`${this.SALES_DASHBOARD_API}/salesdashboard/currencies`, { params: queryParams })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getDashboardCards(filters: {
        region?: string | number | Array<string | number>;
        countryCode?: string | string[] | string[];
        currency?: string;
        productLine?: string | string[] | string[];
        year?: number;
        month?: number;
    }): Observable<any> {
        let queryParams = new HttpParams();
        
                if (filters.region !== undefined && filters.region !== null && filters.region !== '' && filters.region !== 0 && !(Array.isArray(filters.region) && filters.region.length === 0)) {
                    if (Array.isArray(filters.region)) {
                        filters.region.forEach(r => queryParams = queryParams.append('region', String(r)));
                    } else {
                        queryParams = queryParams.set('region', String(filters.region));
                    }
                }
        if (filters.countryCode) {
            if (Array.isArray(filters.countryCode)) {
                filters.countryCode.forEach(c => queryParams = queryParams.append('countryCode', String(c)));
            } else {
                queryParams = queryParams.set('countryCode', String(filters.countryCode));
            }
        }
        if (filters.currency) {
            queryParams = queryParams.set('currency', filters.currency);
        }
        if (filters.productLine) {
            if (Array.isArray(filters.productLine)) {
                filters.productLine.forEach(p => queryParams = queryParams.append('productLine', String(p)));
            } else {
                queryParams = queryParams.set('productLine', String(filters.productLine));
            }
        }
        if (filters.year) {
            queryParams = queryParams.set('year', filters.year);
        }
        if (filters.month) {
            queryParams = queryParams.set('month', filters.month);
        }
        return this.http
            .get<any>(`${this.SALES_DASHBOARD_API}/salesdashboard/cards`, { params: queryParams })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getCategories(): Observable<any[]> {
        return this.http
            .get<any[]>(`${this.SALES_DASHBOARD_API}/salesdashboard/categories`)
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getSubcategories(): Observable<any[]> {
        return this.http
            .get<any[]>(`${this.SALES_DASHBOARD_API}/salesdashboard/subcategories`)
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getRetailerSellout(filters: {
        region?: string | number | Array<string | number>;
        countryCode?: string | string[] | string[];
        currency?: string;
        productLine?: string | string[] | string[];
        year?: number;
        month?: number;
    }): Observable<any> {
        let queryParams = new HttpParams();
        
                if (filters.region !== undefined && filters.region !== null && filters.region !== '' && filters.region !== 0 && !(Array.isArray(filters.region) && filters.region.length === 0)) {
                    if (Array.isArray(filters.region)) {
                        filters.region.forEach(r => queryParams = queryParams.append('region', String(r)));
                    } else {
                        queryParams = queryParams.set('region', String(filters.region));
                    }
                }
        
                if (filters.countryCode !== undefined && filters.countryCode !== null && filters.countryCode !== '' && !(Array.isArray(filters.countryCode) && filters.countryCode.length === 0)) {
                    if (Array.isArray(filters.countryCode)) {
                        filters.countryCode.forEach(c => queryParams = queryParams.append('countryCode', String(c)));
                    } else {
                        queryParams = queryParams.set('countryCode', String(filters.countryCode));
                    }
                }
        if (filters.currency !== undefined && filters.currency !== null && filters.currency !== '') {
            queryParams = queryParams.set('currency', filters.currency);
        }
        
                if (filters.productLine !== undefined && filters.productLine !== null && filters.productLine !== '' && !(Array.isArray(filters.productLine) && filters.productLine.length === 0)) {
                    if (Array.isArray(filters.productLine)) {
                        filters.productLine.forEach(p => queryParams = queryParams.append('productLine', String(p)));
                    } else {
                        queryParams = queryParams.set('productLine', String(filters.productLine));
                    }
                }
        if (filters.year !== undefined && filters.year !== null) {
            queryParams = queryParams.set('year', filters.year.toString());
        }
        if (filters.month) {
            queryParams = queryParams.set('month', filters.month.toString());
        }
        return this.http
            .get<any>(`${this.SALES_DASHBOARD_API}/salesdashboard/retailer-sellout`, { params: queryParams })
            .pipe(catchError(this.errorHandler.handleError));
    }



    public getRegionSummary(filters: {
        region?: string | number | Array<string | number>;
        countryCode?: string | string[] | string[];
        currency?: string;
        productLine?: string | string[] | string[];
        period?: string;
    }): Observable<any> {
        let queryParams = new HttpParams();
        
                if (filters.region !== undefined && filters.region !== null && filters.region !== '' && filters.region !== 0 && !(Array.isArray(filters.region) && filters.region.length === 0)) {
                    if (Array.isArray(filters.region)) {
                        filters.region.forEach(r => queryParams = queryParams.append('region', String(r)));
                    } else {
                        queryParams = queryParams.set('region', String(filters.region));
                    }
                }
        if (filters.countryCode) {
            if (Array.isArray(filters.countryCode)) {
                filters.countryCode.forEach(c => queryParams = queryParams.append('countryCode', String(c)));
            } else {
                queryParams = queryParams.set('countryCode', String(filters.countryCode));
            }
        }
        if (filters.currency) {
            queryParams = queryParams.set('currency', filters.currency);
        }
        if (filters.productLine) {
            if (Array.isArray(filters.productLine)) {
                filters.productLine.forEach(p => queryParams = queryParams.append('productLine', String(p)));
            } else {
                queryParams = queryParams.set('productLine', String(filters.productLine));
            }
        }
        if (filters.period) {
            queryParams = queryParams.set('period', filters.period);
        }
        return this.http
            .get<any>(`${this.SALES_DASHBOARD_API}/salesdashboard/regionsummary`, { params: queryParams })
            .pipe(catchError(this.errorHandler.handleError));
    }


    public getCountryRanking(filters: {
        region?: string | number | Array<string | number>;
        countryCode?: string | string[] | string[];
        currency?: string;
        productLine?: string | string[] | string[];
        metric?: string;
        type?: string;
        period?: string;
    }): Observable<any> {
        let queryParams = new HttpParams();
        
                if (filters.region !== undefined && filters.region !== null && filters.region !== '' && filters.region !== 0 && !(Array.isArray(filters.region) && filters.region.length === 0)) {
                    if (Array.isArray(filters.region)) {
                        filters.region.forEach(r => queryParams = queryParams.append('region', String(r)));
                    } else {
                        queryParams = queryParams.set('region', String(filters.region));
                    }
                }
        if (filters.countryCode) {
            if (Array.isArray(filters.countryCode)) {
                filters.countryCode.forEach(c => queryParams = queryParams.append('countryCode', String(c)));
            } else {
                queryParams = queryParams.set('countryCode', String(filters.countryCode));
            }
        }
        if (filters.currency) {
            queryParams = queryParams.set('currency', filters.currency);
        }
        if (filters.productLine) {
            if (Array.isArray(filters.productLine)) {
                filters.productLine.forEach(p => queryParams = queryParams.append('productLine', String(p)));
            } else {
                queryParams = queryParams.set('productLine', String(filters.productLine));
            }
        }
        if (filters.metric) {
            queryParams = queryParams.set('metric', filters.metric);
        }
        if (filters.type) {
            queryParams = queryParams.set('type', filters.type);
        }
        if (filters.period) {
            queryParams = queryParams.set('period', filters.period);
        }

        return this.http
            .get<any>(`${this.SALES_DASHBOARD_API}/salesdashboard/country-ranking`, { params: queryParams })
            .pipe(catchError(this.errorHandler.handleError));
    }



    public getProductMix(filters: {
        region?: string | number | Array<string | number>;
        countryCode?: string | string[] | string[];
        currency?: string;
        productLine?: string | string[] | string[];
        level?: string;
        parentName?: string;
        period?: string;
    }): Observable<any> {
        let queryParams = new HttpParams();
        
                if (filters.region !== undefined && filters.region !== null && filters.region !== '' && filters.region !== 0 && !(Array.isArray(filters.region) && filters.region.length === 0)) {
                    if (Array.isArray(filters.region)) {
                        filters.region.forEach(r => queryParams = queryParams.append('region', String(r)));
                    } else {
                        queryParams = queryParams.set('region', String(filters.region));
                    }
                }
        if (filters.countryCode) {
            if (Array.isArray(filters.countryCode)) {
                filters.countryCode.forEach(c => queryParams = queryParams.append('countryCode', String(c)));
            } else {
                queryParams = queryParams.set('countryCode', String(filters.countryCode));
            }
        }
        if (filters.currency) {
            queryParams = queryParams.set('currency', filters.currency);
        }
        if (filters.productLine) {
            if (Array.isArray(filters.productLine)) {
                filters.productLine.forEach(p => queryParams = queryParams.append('productLine', String(p)));
            } else {
                queryParams = queryParams.set('productLine', String(filters.productLine));
            }
        }
        if (filters.level) {
            queryParams = queryParams.set('level', filters.level);
        }
        if (filters.parentName) {
            queryParams = queryParams.set('parentName', filters.parentName);
        }
        if (filters.period) {
            queryParams = queryParams.set('period', filters.period);
        }

        return this.http
            .get<any>(`${this.SALES_DASHBOARD_API}/salesdashboard/product-mix`, { params: queryParams })
            .pipe(catchError(this.errorHandler.handleError));
    }


    public getProductMixTrend(filters: {
        region?: string | number | Array<string | number>;
        countryCode?: string | string[] | string[];
        currency?: string;
        productLine?: string | string[] | string[];
        year?: number;
        month?: number;
        period?: string;
    }): Observable<any> {
        let queryParams = new HttpParams();
        
                if (filters.region !== undefined && filters.region !== null && filters.region !== '' && filters.region !== 0 && !(Array.isArray(filters.region) && filters.region.length === 0)) {
                    if (Array.isArray(filters.region)) {
                        filters.region.forEach(r => queryParams = queryParams.append('region', String(r)));
                    } else {
                        queryParams = queryParams.set('region', String(filters.region));
                    }
                }
        if (filters.countryCode) {
            if (Array.isArray(filters.countryCode)) {
                filters.countryCode.forEach(c => queryParams = queryParams.append('countryCode', String(c)));
            } else {
                queryParams = queryParams.set('countryCode', String(filters.countryCode));
            }
        }
        if (filters.currency) {
            queryParams = queryParams.set('currency', filters.currency);
        }
        if (filters.productLine) {
            if (Array.isArray(filters.productLine)) {
                filters.productLine.forEach(p => queryParams = queryParams.append('productLine', String(p)));
            } else {
                queryParams = queryParams.set('productLine', String(filters.productLine));
            }
        }
        if (filters.month) {
            queryParams = queryParams.set('month', filters.month);
        }
        if (filters.year) {
            queryParams = queryParams.set('year', filters.year);
        }
        if (filters.period) {
            queryParams = queryParams.set('period', filters.period);
        }

        return this.http
            .get<any>(`${this.SALES_DASHBOARD_API}/salesdashboard/product-mix-trend`, { params: queryParams })
            .pipe(catchError(this.errorHandler.handleError));
    }




    public getMonthlyTrendMatrix(filters: {
        region?: string | number | Array<string | number>;
        countryCode?: string | string[] | string[];
        currency?: string;
        productLine?: string | string[] | string[];
        metric?: string;
        mode?: string;
        year?: number;
        month?: number;
        period?: string;
    }): Observable<any> {
        let queryParams = new HttpParams();
        
                if (filters.region !== undefined && filters.region !== null && filters.region !== '' && filters.region !== 0 && !(Array.isArray(filters.region) && filters.region.length === 0)) {
                    if (Array.isArray(filters.region)) {
                        filters.region.forEach(r => queryParams = queryParams.append('region', String(r)));
                    } else {
                        queryParams = queryParams.set('region', String(filters.region));
                    }
                }
        if (filters.countryCode) {
            if (Array.isArray(filters.countryCode)) {
                filters.countryCode.forEach(c => queryParams = queryParams.append('countryCode', String(c)));
            } else {
                queryParams = queryParams.set('countryCode', String(filters.countryCode));
            }
        }
        if (filters.currency) {
            queryParams = queryParams.set('currency', filters.currency);
        }
        if (filters.productLine) {
            if (Array.isArray(filters.productLine)) {
                filters.productLine.forEach(p => queryParams = queryParams.append('productLine', String(p)));
            } else {
                queryParams = queryParams.set('productLine', String(filters.productLine));
            }
        }
        if (filters.month) {
            queryParams = queryParams.set('month', filters.month);
        }
        if (filters.year) {
            queryParams = queryParams.set('year', filters.year);
        }
        if (filters.metric) {
            queryParams = queryParams.set('metric', filters.metric);
        }
        if (filters.mode) {
            queryParams = queryParams.set('view', filters.mode);
        }
        if (filters.period) {
            queryParams = queryParams.set('period', filters.period);
        }

        return this.http
            .get<any>(`${this.SALES_DASHBOARD_API}/salesdashboard/monthly-trend-matrix`, { params: queryParams })
            .pipe(catchError(this.errorHandler.handleError));
    }



    public exportTrendMetricDetails(filters: {
        region?: string | number | Array<string | number>;
        countryCode?: string | string[] | string[];
        currency?: string;
        productLine?: string | string[] | string[];
        metric?: string;
        mode?: string;
        year?: number;
        month?: number;
        period?: string;
    }): Observable<any> {
        let queryParams = new HttpParams();
        if (filters.region !== undefined && filters.region !== null && filters.region !== '' && filters.region !== 0 && !(Array.isArray(filters.region) && filters.region.length === 0)) {
            if (Array.isArray(filters.region)) {
                // join array into comma-separated list
                const val = filters.region.join(',');
                queryParams = queryParams.set('region', val);
            } else {
                queryParams = queryParams.set('region', String(filters.region));
            }
        }
        if (filters.countryCode) {
            if (Array.isArray(filters.countryCode)) {
                filters.countryCode.forEach(c => queryParams = queryParams.append('countryCode', String(c)));
            } else {
                queryParams = queryParams.set('countryCode', String(filters.countryCode));
            }
        }
        if (filters.currency) {
            queryParams = queryParams.set('currency', filters.currency);
        }
        if (filters.productLine) {
            if (Array.isArray(filters.productLine)) {
                filters.productLine.forEach(p => queryParams = queryParams.append('productLine', String(p)));
            } else {
                queryParams = queryParams.set('productLine', String(filters.productLine));
            }
        }
        if (filters.month) {
            queryParams = queryParams.set('month', filters.month);
        }
        if (filters.year) {
            queryParams = queryParams.set('year', filters.year);
        }
        if (filters.metric) {
            queryParams = queryParams.set('metric', filters.metric);
        }
        if (filters.mode) {
            queryParams = queryParams.set('view', filters.mode);
        }
        if (filters.period) {
            queryParams = queryParams.set('period', filters.period);
        }

        return this.http
            .post<Blob>(
                `${this.SALES_DASHBOARD_API}/salesdashboard/monthly-trend-matrix/downloadExcelReport`,
                null,
                {
                    params: queryParams,
                    responseType: 'blob' as 'json'
                }
            )
            .pipe(catchError(this.errorHandler.handleError));
    }


    public getSummarySnapSots(filters: {
        region?: string | number | Array<string | number>;
        countryCode?: string | string[] | string[];
        currency?: string;
        productLine?: string | string[] | string[];
        year?: number;
        month?: number;
        period?: string;
    }): Observable<any> {
        let queryParams = new HttpParams();
        
                if (filters.region !== undefined && filters.region !== null && filters.region !== '' && filters.region !== 0 && !(Array.isArray(filters.region) && filters.region.length === 0)) {
                    if (Array.isArray(filters.region)) {
                        filters.region.forEach(r => queryParams = queryParams.append('region', String(r)));
                    } else {
                        queryParams = queryParams.set('region', String(filters.region));
                    }
                }
        if (filters.countryCode) {
            if (Array.isArray(filters.countryCode)) {
                filters.countryCode.forEach(c => queryParams = queryParams.append('countryCode', String(c)));
            } else {
                queryParams = queryParams.set('countryCode', String(filters.countryCode));
            }
        }
        if (filters.currency) {
            queryParams = queryParams.set('currency', filters.currency);
        }
        if (filters.productLine) {
            if (Array.isArray(filters.productLine)) {
                filters.productLine.forEach(p => queryParams = queryParams.append('productLine', String(p)));
            } else {
                queryParams = queryParams.set('productLine', String(filters.productLine));
            }
        }
        if (filters.year) {
            queryParams = queryParams.set('year', filters.year);
        }
        if (filters.month) {
            queryParams = queryParams.set('month', filters.month);
        }
        if (filters.period) {
            queryParams = queryParams.set('period', filters.period)
        }
        return this.http
            .get<any>(`${this.SALES_DASHBOARD_API}/salesdashboard/executive-snapshot`, { params: queryParams })
            .pipe(catchError(this.errorHandler.handleError));
    }


    public getCountryContribution(filters: {
        region?: string | number | Array<string | number>;
        countryCode?: string | string[] | string[];
        currency?: string;
        productLine?: string | string[] | string[];
        year?: number;
        month?: number;
        period?: string;
    }): Observable<any> {
        let queryParams = new HttpParams();
        
                if (filters.region !== undefined && filters.region !== null && filters.region !== '' && filters.region !== 0 && !(Array.isArray(filters.region) && filters.region.length === 0)) {
                    if (Array.isArray(filters.region)) {
                        filters.region.forEach(r => queryParams = queryParams.append('region', String(r)));
                    } else {
                        queryParams = queryParams.set('region', String(filters.region));
                    }
                }
        if (filters.countryCode) {
            if (Array.isArray(filters.countryCode)) {
                filters.countryCode.forEach(c => queryParams = queryParams.append('countryCode', String(c)));
            } else {
                queryParams = queryParams.set('countryCode', String(filters.countryCode));
            }
        }
        if (filters.currency) {
            queryParams = queryParams.set('currency', filters.currency);
        }
        if (filters.productLine) {
            if (Array.isArray(filters.productLine)) {
                filters.productLine.forEach(p => queryParams = queryParams.append('productLine', String(p)));
            } else {
                queryParams = queryParams.set('productLine', String(filters.productLine));
            }
        }
        if (filters.year) {
            queryParams = queryParams.set('year', filters.year);
        }
        if (filters.month) {
            queryParams = queryParams.set('month', filters.month);
        }
        if (filters.period) {
            queryParams = queryParams.set('period', filters.period)
        }
        return this.http
            .get<any>(`${this.SALES_DASHBOARD_API}/salesdashboard/sellin-contribution`, { params: queryParams })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getAdminSelloutFilterOptions(filters: {
        region?: string[];
        country?: string[];
        year?: string[];
    } = {}): Observable<any> {
        return this.http
            .get<any>(`${this.SALES_DASHBOARD_API}/sellout-dashboard/filter-options`, {
                params: this.buildSelloutDashboardParams(filters),
            })
            .pipe(
                map((res) => res?.result ?? res),
                catchError(this.errorHandler.handleError),
            );
    }

    public getAdminSelloutCascadeFilterOptions(filters: {
        region?: string[];
        country?: string[];
        account?: string[];
        category?: string[];
        year?: string[];
    } = {}): Observable<any> {
        return this.http
            .get<any>(`${this.SALES_DASHBOARD_API}/sellout-dashboard/cascade-filter-options`, {
                params: this.buildSelloutDashboardParams(filters),
            })
            .pipe(
                map((res) => res?.result ?? res),
                catchError(this.errorHandler.handleError),
            );
    }

    private buildSelloutDashboardParams(
        filters: Record<string, string[] | undefined>,
    ): HttpParams {
        let queryParams = new HttpParams();
        Object.entries(filters).forEach(([key, values]) => {
            if (values?.length) {
                queryParams = queryParams.set(key, values.join(','));
            }
        });
        return queryParams;
    }

    public getAdminSelloutDashboard(filters: {
        region?: string[];
        country?: string[];
        account?: string[];
        category?: string[];
        subcategory?: string[];
        store?: string[];
        year?: string[];
        month?: string[];
    } = {}): Observable<any> {
        return this.http
            .get<any>(`${this.SALES_DASHBOARD_API}/sellout-dashboard/dashboard`, {
                params: this.buildSelloutDashboardParams(filters),
            })
            .pipe(
                map((res) => res?.result ?? res),
                catchError(this.errorHandler.handleError),
            );
    }

    public getAdminSelloutDashboardWidgets(filters: {
        region?: string[];
        country?: string[];
        account?: string[];
        category?: string[];
        subcategory?: string[];
        store?: string[];
        year?: string[];
        month?: string[];
    } = {}) {
        const params = this.buildSelloutDashboardParams(filters);
        const base = `${this.SALES_DASHBOARD_API}/sellout-dashboard/dashboard`;
        const mapResult = (res: any) => res?.result ?? res;

        return {
            metrics: this.http.get<any>(`${base}/metrics`, { params }).pipe(
                map(mapResult),
                catchError(this.errorHandler.handleError),
            ),
            monthlyTrend: this.http.get<any>(`${base}/monthly-trend`, { params }).pipe(
                map(mapResult),
                catchError(this.errorHandler.handleError),
            ),
            topStores: this.http.get<any>(`${base}/top-stores`, { params }).pipe(
                map(mapResult),
                catchError(this.errorHandler.handleError),
            ),
            topProducts: this.http.get<any>(`${base}/top-products`, { params }).pipe(
                map(mapResult),
                catchError(this.errorHandler.handleError),
            ),
            categories: this.http.get<any>(`${base}/categories`, { params }).pipe(
                map(mapResult),
                catchError(this.errorHandler.handleError),
            ),
            subcategories: this.http.get<any>(`${base}/subcategories`, { params }).pipe(
                map(mapResult),
                catchError(this.errorHandler.handleError),
            ),
            accounts: this.http.get<any>(`${base}/accounts`, { params }).pipe(
                map(mapResult),
                catchError(this.errorHandler.handleError),
            ),
        };
    }




    public getFieldForceDashboardCards(filters: {
        region?: string | number | Array<string | number>;
        countryCode?: string | string[] | string[];
        currency?: string;
        period?: string;
    }): Observable<any> {
        let queryParams = new HttpParams();
        if (filters.region !== undefined && filters.region !== null && filters.region !== '' && filters.region !== 0 && !(Array.isArray(filters.region) && filters.region.length === 0)) {
            if (Array.isArray(filters.region)) {
                // join array into comma-separated list
                const val = filters.region.join(',');
                queryParams = queryParams.set('region', val);
            } else {
                queryParams = queryParams.set('region', String(filters.region));
            }
        }
        if (filters.countryCode) {
            if (Array.isArray(filters.countryCode)) {
                filters.countryCode.forEach(c => queryParams = queryParams.append('countryCode', String(c)));
            } else {
                queryParams = queryParams.set('countryCode', String(filters.countryCode));
            }
        }
        if (filters.currency) {
            queryParams = queryParams.set('currency', filters.currency);
        }

        if (filters.period) {
            queryParams = queryParams.set('period', filters.period)
        }
        return this.http
            .get<any>(`${this.SALES_DASHBOARD_API}/salesdashboard/field-force-cards`, { params: queryParams })
            .pipe(catchError(this.errorHandler.handleError));
    }

    // --- TARGET SETUP APIs ---
    public getMysteryShoppingScores(filters?: any): Observable<MysteryShoppingScore[]> {
        let params = new HttpParams();
        if (filters?.region && filters.region.length > 0) params = params.set('region', Array.isArray(filters.region) ? filters.region.join(',') : filters.region);
        if (filters?.country && filters.country.length > 0) params = params.set('country', Array.isArray(filters.country) ? filters.country.join(',') : filters.country);
        if (filters?.productLine && filters.productLine.length > 0) params = params.set('productLine', Array.isArray(filters.productLine) ? filters.productLine.join(',') : filters.productLine);
        if (filters?.category && filters.category.length > 0) params = params.set('category', Array.isArray(filters.category) ? filters.category.join(',') : filters.category);
        if (filters?.subcategory && filters.subcategory.length > 0) params = params.set('subcategory', Array.isArray(filters.subcategory) ? filters.subcategory.join(',') : filters.subcategory);
        if (filters?.year) params = params.set('year', Array.isArray(filters.year) ? filters.year.join(',') : filters.year);
        return this.http.get<MysteryShoppingScore[]>(`${this.SALES_DASHBOARD_API}/salesdashboard/targetsetup/mystery-shopping`, { params })
            .pipe(catchError(this.errorHandler.handleError));
    }
    public saveMysteryShoppingScore(data: any): Observable<any> {
        return this.http.post<any>(`${this.SALES_DASHBOARD_API}/salesdashboard/targetsetup/mystery-shopping`, data)
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getPromoterEfficiencyTargets(filters?: any): Observable<PromoterEfficiencyTarget[]> {
        let params = new HttpParams();
        if (filters?.region && filters.region.length > 0) params = params.set('region', Array.isArray(filters.region) ? filters.region.join(',') : filters.region);
        if (filters?.country && filters.country.length > 0) params = params.set('country', Array.isArray(filters.country) ? filters.country.join(',') : filters.country);
        if (filters?.year) params = params.set('year', Array.isArray(filters.year) ? filters.year.join(',') : filters.year);
        return this.http.get<PromoterEfficiencyTarget[]>(`${this.SALES_DASHBOARD_API}/salesdashboard/targetsetup/promoter-efficiency`, { params })
            .pipe(catchError(this.errorHandler.handleError));
    }
    public savePromoterEfficiencyTarget(data: any): Observable<any> {
        return this.http.post<any>(`${this.SALES_DASHBOARD_API}/salesdashboard/targetsetup/promoter-efficiency`, data)
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getSelloutTargets(filters?: any): Observable<SelloutTarget[]> {
        let params = new HttpParams();
        if (filters?.region && filters.region.length > 0) params = params.set('region', Array.isArray(filters.region) ? filters.region.join(',') : filters.region);
        if (filters?.country && filters.country.length > 0) params = params.set('country', Array.isArray(filters.country) ? filters.country.join(',') : filters.country);
        if (filters?.productLine && filters.productLine.length > 0) params = params.set('productLine', Array.isArray(filters.productLine) ? filters.productLine.join(',') : filters.productLine);
        if (filters?.category && filters.category.length > 0) params = params.set('category', Array.isArray(filters.category) ? filters.category.join(',') : filters.category);
        if (filters?.subcategory && filters.subcategory.length > 0) params = params.set('subcategory', Array.isArray(filters.subcategory) ? filters.subcategory.join(',') : filters.subcategory);
        if (filters?.year) params = params.set('year', Array.isArray(filters.year) ? filters.year.join(',') : filters.year);
        return this.http.get<SelloutTarget[]>(`${this.SALES_DASHBOARD_API}/salesdashboard/targetsetup/sellout-target`, { params })
            .pipe(catchError(this.errorHandler.handleError));
    }
    public saveSelloutTarget(data: any): Observable<any> {
        return this.http.post<any>(`${this.SALES_DASHBOARD_API}/salesdashboard/targetsetup/sellout-target`, data)
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getTrainingScores(filters?: any): Observable<TrainingScore[]> {
        let params = new HttpParams();
        if (filters?.region && filters.region.length > 0) params = params.set('region', Array.isArray(filters.region) ? filters.region.join(',') : filters.region);
        if (filters?.country && filters.country.length > 0) params = params.set('country', Array.isArray(filters.country) ? filters.country.join(',') : filters.country);
        if (filters?.productLine && filters.productLine.length > 0) params = params.set('productLine', Array.isArray(filters.productLine) ? filters.productLine.join(',') : filters.productLine);
        if (filters?.category && filters.category.length > 0) params = params.set('category', Array.isArray(filters.category) ? filters.category.join(',') : filters.category);
        if (filters?.subcategory && filters.subcategory.length > 0) params = params.set('subcategory', Array.isArray(filters.subcategory) ? filters.subcategory.join(',') : filters.subcategory);
        if (filters?.year) params = params.set('year', Array.isArray(filters.year) ? filters.year.join(',') : filters.year);
        return this.http.get<TrainingScore[]>(`${this.SALES_DASHBOARD_API}/salesdashboard/targetsetup/training-score`, { params })
            .pipe(catchError(this.errorHandler.handleError));
    }
    public saveTrainingScore(data: any): Observable<any> {
        return this.http.post<any>(`${this.SALES_DASHBOARD_API}/salesdashboard/targetsetup/training-score`, data)
            .pipe(catchError(this.errorHandler.handleError));
    }

    public deleteTarget(type: string, id: string | number): Observable<any> {
        let endpoint = '';
        switch (type) {
            case 'mystery': endpoint = 'mystery-shopping'; break;
            case 'promoter': endpoint = 'promoter-efficiency'; break;
            case 'sellout': endpoint = 'sellout-target'; break;
            case 'training': endpoint = 'training-score'; break;
            case 'mmp': endpoint = 'mmp-target'; break;
            case 'sellin': endpoint = 'sellin-target'; break;
        }
        return this.http.delete<any>(`${this.SALES_DASHBOARD_API}/salesdashboard/targetsetup/${endpoint}/${id}`)
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getMmpTargets(filters?: any): Observable<any[]> {
        let params = new HttpParams();
        if (filters?.region && filters.region.length > 0) params = params.set('region', Array.isArray(filters.region) ? filters.region.join(',') : filters.region);
        if (filters?.country && filters.country.length > 0) params = params.set('country', Array.isArray(filters.country) ? filters.country.join(',') : filters.country);
        if (filters?.category && filters.category.length > 0) params = params.set('category', Array.isArray(filters.category) ? filters.category.join(',') : filters.category);
        if (filters?.subcategory && filters.subcategory.length > 0) params = params.set('subcategory', Array.isArray(filters.subcategory) ? filters.subcategory.join(',') : filters.subcategory);
        if (filters?.year) params = params.set('year', Array.isArray(filters.year) ? filters.year.join(',') : filters.year);
        return this.http.get<any[]>(`${this.SALES_DASHBOARD_API}/salesdashboard/targetsetup/mmp-target`, { params })
            .pipe(catchError(this.errorHandler.handleError));
    }
    public saveMmpTarget(data: any): Observable<any> {
        return this.http.post<any>(`${this.SALES_DASHBOARD_API}/salesdashboard/targetsetup/mmp-target`, data)
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getSellInTargets(filters?: any): Observable<any[]> {
        let params = new HttpParams();
        if (filters?.region && filters.region.length > 0) params = params.set('region', Array.isArray(filters.region) ? filters.region.join(',') : filters.region);
        if (filters?.country && filters.country.length > 0) params = params.set('country', Array.isArray(filters.country) ? filters.country.join(',') : filters.country);
        if (filters?.category && filters.category.length > 0) params = params.set('category', Array.isArray(filters.category) ? filters.category.join(',') : filters.category);
        if (filters?.subcategory && filters.subcategory.length > 0) params = params.set('subcategory', Array.isArray(filters.subcategory) ? filters.subcategory.join(',') : filters.subcategory);
        if (filters?.year) params = params.set('year', Array.isArray(filters.year) ? filters.year.join(',') : filters.year);
        return this.http.get<any[]>(`${this.SALES_DASHBOARD_API}/salesdashboard/targetsetup/sellin-target`, { params })
            .pipe(catchError(this.errorHandler.handleError));
    }
    public saveSellInTarget(data: any): Observable<any> {
        return this.http.post<any>(`${this.SALES_DASHBOARD_API}/salesdashboard/targetsetup/sellin-target`, data)
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getRetailerProducts(filters: {
        pageIndex?: number;
        pageSize?: number;
        searchValue?: string;
        brands?: string[];
        productLineId?: (string | number)[];
        productGroupId?: (string | number)[];
        categoryId?: (string | number)[];
        subCategoryId?: (string | number)[];
        projectId?: (string | number)[];
        capacityId?: (string | number)[];
        inRange?: string[];
        country?: string[];
        account?: string[];
    }): Observable<any> {
        let params = new HttpParams();

        if (filters.pageIndex !== undefined) {
            params = params.set('pageIndex', filters.pageIndex.toString());
        }
        if (filters.pageSize !== undefined) {
            params = params.set('pageSize', filters.pageSize.toString());
        }
        if (filters.searchValue?.trim()) {
            params = params.set('searchValue', filters.searchValue.trim());
        }
        if (filters.brands && filters.brands.length > 0) {
            params = params.set('brands', filters.brands.join(','));
        }
        if (filters.productLineId && filters.productLineId.length > 0) {
            params = params.set('productLineId', filters.productLineId.join(','));
        }
        if (filters.productGroupId && filters.productGroupId.length > 0) {
            params = params.set('productGroupId', filters.productGroupId.join(','));
        }
        if (filters.categoryId && filters.categoryId.length > 0) {
            params = params.set('categoryId', filters.categoryId.join(','));
        }
        if (filters.subCategoryId && filters.subCategoryId.length > 0) {
            params = params.set('subCategoryId', filters.subCategoryId.join(','));
        }
        if (filters.projectId && filters.projectId.length > 0) {
            params = params.set('projectId', filters.projectId.join(','));
        }
        if (filters.capacityId && filters.capacityId.length > 0) {
            params = params.set('capacityId', filters.capacityId.join(','));
        }
        if (filters.inRange && filters.inRange.length > 0) {
            params = params.set('inRange', filters.inRange.join(','));
        }
        if (filters.country && filters.country.length > 0) {
            params = params.set('country', filters.country.join(','));
        }
        if (filters.account && filters.account.length > 0) {
            params = params.set('account', filters.account.join(','));
        }

        return this.http
            .get<any>(`${this.SALES_DASHBOARD_API}/retailer-sync/products`, { params })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public exportRetailerProducts(filters: {
        searchValue?: string;
        brands?: string[];
        productLineId?: (string | number)[];
        productGroupId?: (string | number)[];
        categoryId?: (string | number)[];
        subCategoryId?: (string | number)[];
        projectId?: (string | number)[];
        capacityId?: (string | number)[];
        inRange?: string[];
        country?: string[];
    }): Observable<Blob> {
        let params = new HttpParams();
        if (filters.searchValue?.trim()) params = params.set('searchValue', filters.searchValue.trim());
        if (filters.brands?.length) params = params.set('brands', filters.brands.join(','));
        if (filters.productLineId?.length) params = params.set('productLineId', filters.productLineId.join(','));
        if (filters.productGroupId?.length) params = params.set('productGroupId', filters.productGroupId.join(','));
        if (filters.categoryId?.length) params = params.set('categoryId', filters.categoryId.join(','));
        if (filters.subCategoryId?.length) params = params.set('subCategoryId', filters.subCategoryId.join(','));
        if (filters.projectId?.length) params = params.set('projectId', filters.projectId.join(','));
        if (filters.capacityId?.length) params = params.set('capacityId', filters.capacityId.join(','));
        if (filters.inRange?.length) params = params.set('inRange', filters.inRange.join(','));
        if (filters.country?.length) params = params.set('country', filters.country.join(','));

        return this.http
            .get(`${this.SALES_DASHBOARD_API}/retailer-sync/export`, { params, responseType: 'blob' })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getRetailerStoreMetadata(): Observable<any> {
        return this.http
            .get<any>(`${this.SALES_DASHBOARD_API}/retailer-sync/stores/metadata`)
            .pipe(catchError(this.errorHandler.handleError));
    }

    public getRetailerStores(filters: {
        pageIndex?: number;
        pageSize?: number;
        searchValue?: string;
        region?: string[];
        country?: string[];
        channel?: string[];
        account?: string[];
        selloutType?: string[];
    }): Observable<any> {
        let params = new HttpParams();
        if (filters.pageIndex !== undefined) params = params.set('pageIndex', filters.pageIndex.toString());
        if (filters.pageSize !== undefined) params = params.set('pageSize', filters.pageSize.toString());
        if (filters.searchValue?.trim()) params = params.set('searchValue', filters.searchValue.trim());
        if (filters.region?.length) params = params.set('region', filters.region.join(','));
        if (filters.country?.length) params = params.set('country', filters.country.join(','));
        if (filters.channel?.length) params = params.set('channel', filters.channel.join(','));
        if (filters.account?.length) params = params.set('account', filters.account.join(','));
        if (filters.selloutType?.length) params = params.set('selloutType', filters.selloutType.join(','));

        return this.http
            .get<any>(`${this.SALES_DASHBOARD_API}/retailer-sync/stores`, { params })
            .pipe(catchError(this.errorHandler.handleError));
    }

    public exportRetailerStores(filters: {
        searchValue?: string;
        region?: string[];
        country?: string[];
        channel?: string[];
        account?: string[];
        selloutType?: string[];
    }): Observable<Blob> {
        let params = new HttpParams();
        if (filters.searchValue?.trim()) params = params.set('searchValue', filters.searchValue.trim());
        if (filters.region?.length) params = params.set('region', filters.region.join(','));
        if (filters.country?.length) params = params.set('country', filters.country.join(','));
        if (filters.channel?.length) params = params.set('channel', filters.channel.join(','));
        if (filters.account?.length) params = params.set('account', filters.account.join(','));
        if (filters.selloutType?.length) params = params.set('selloutType', filters.selloutType.join(','));

        return this.http
            .get(`${this.SALES_DASHBOARD_API}/retailer-sync/stores/export`, { params, responseType: 'blob' })
            .pipe(catchError(this.errorHandler.handleError));
    }
}

// --- TARGET SETUP MODELS ---
export interface MysteryShoppingScore {
  year: number;
  month_number: number;
  date: string;
  country: string;
  adj_product_category_id: string;
  target: string;
  score: string;
  exceed: number;
}

export interface PromoterEfficiencyTarget {
  year: number;
  month_number: number;
  country: string;
  target_value: string;
}

export interface SelloutTarget {
  year: number;
  month_number: number;
  country: string;
  adj_product_category_id: string;
  adj_product_subcategory_id: string;
  fy_target: string;
  exceed: string;
  target_value: string;
}

export interface TrainingScore {
  year: number;
  month_number: number;
  date: string;
  country: string;
  adj_product_category_id: string;
  target: string;
  exceed: string;
  score: string;
}


